/**
 * ReferrerPicker — "Who sent you?", asked at signup, on every path in.
 *
 * Oct 2026 (Randy, seeding pass). Seeding decides who earns from a new member
 * for as long as they stay, and until now it was decided silently: the fan
 * never learned who was credited, the creator never learned they'd lost it,
 * and "last silo visited wins" quietly handed the credit to the third silo
 * somebody browsed out of curiosity rather than the friend who sent them.
 *
 * So we show the guess and let them correct it:
 *   Yes          → confirm the detected creator (freezes it)
 *   Someone else → choose from the silos THIS DEVICE has actually visited
 *   I found My Silo myself → the house takes it, like any cold arrival. NOT
 *                  frozen, deliberately: the first creator they actually
 *                  engage with can still inherit them before they pay, which
 *                  is how credit moves off the house and onto creators.
 *
 * The list is the entire fraud boundary: it can only ever be built from
 * observed visits. There is no search box and no name field — the only way
 * into somebody's chooser is to have opened their page, which is the behaviour
 * the platform is supposed to reward anyway.
 */
import { useEffect, useMemo, useState } from "react";
import { CaretDown, CheckCircle, UserCircle, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { getVisitedSilos } from "@/lib/attribution";

const INTENT_LABEL = {
  fan: "Signing up as a fan",
  creator: "Signing up as a creator",
  trial: "Signing up for the trial",
};

export default function ReferrerPicker({ intent, detectedSlug, value, onChange, theme }) {
  const T = theme || {};
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const visited = useMemo(() => getVisitedSilos(), []);

  useEffect(() => {
    // Resolve the device's remembered slugs into names. Detected slug first so
    // it's always offered even if the ring was cleared.
    const slugs = [...new Set([detectedSlug, ...visited].filter(Boolean))];
    if (!slugs.length) { setLoaded(true); return; }
    let cancel = false;
    api.post("/public/visited-silos", { slugs })
      .then((r) => { if (!cancel) setOptions(r.data?.silos || []); })
      .catch(() => { /* resolver down — fall back to "nobody" only */ })
      .finally(() => { if (!cancel) setLoaded(true); });
    return () => { cancel = true; };
  }, [detectedSlug, visited]);

  if (!loaded) return null;

  const detected = options.find((o) => o.slug === detectedSlug) || null;
  const picked = value?.mode === "pick" ? options.find((o) => o.slug === value.slug) : null;
  const others = options.filter((o) => o.slug !== detectedSlug);
  const eyebrow = INTENT_LABEL[intent] || "Signing up";

  const choose = (mode, slug) => { onChange({ mode, slug: slug || null }); setOpen(false); };

  return (
    <div className={T.panelAlt} data-testid="referrer-picker">
      <div className={T.eyebrow}>
        <UserCircle size={12} weight="duotone" /> {eyebrow} — who sent you?
      </div>

      {/* The guess, stated plainly, with the consequence stated plainly. */}
      {detected ? (
        <div className={`text-xs ${T.muted} leading-relaxed`}>
          It looks like you came from <strong className={T.strong}>{detected.name}</strong>
          {detected.creator && detected.creator !== detected.name ? <> by {detected.creator}</> : null}.
          They'll be credited as the creator who brought you to My Silo.
        </div>
      ) : (
        <div className={`text-xs ${T.muted} leading-relaxed`}>
          We don't know who told you about My Silo. If a creator sent you, pick them so they get
          the credit.
        </div>
      )}

      {/* Current answer */}
      {value?.mode === "self_found" && (
        <div className="text-xs mt-2 flex items-center gap-1.5 text-[#4A5D4E]" data-testid="referrer-picker-answer-none">
          <CheckCircle size={13} weight="duotone" /> I found My Silo myself.
        </div>
      )}
      {picked && (
        <div className="text-xs mt-2 flex items-center gap-1.5 text-[#4A5D4E]" data-testid="referrer-picker-answer-pick">
          <CheckCircle size={13} weight="duotone" /> Credit <strong>{picked.name}</strong>
          {picked.creator && picked.creator !== picked.name ? <> ({picked.creator})</> : null}
        </div>
      )}
      {value?.mode === "confirm" && detected && (
        <div className="text-xs mt-2 flex items-center gap-1.5 text-[#4A5D4E]" data-testid="referrer-picker-answer-confirm">
          <CheckCircle size={13} weight="duotone" /> Yes — credit <strong>{detected.name}</strong>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        {detected && value?.mode !== "confirm" && (
          <button
            type="button" onClick={() => choose("confirm")}
            data-testid="referrer-picker-yes"
            className={T.pickerPrimary}
          >
            Yes, that's right
          </button>
        )}
        {(others.length > 0 || !detected) && (
          <button
            type="button" onClick={() => setOpen((v) => !v)}
            data-testid="referrer-picker-someone-else"
            className={T.pickerGhost}
            aria-expanded={open}
          >
            {detected ? "No — someone else" : "Pick a creator"} <CaretDown size={11} weight="bold" />
          </button>
        )}
        {value?.mode !== "self_found" && (
          <button
            type="button" onClick={() => choose("self_found")}
            data-testid="referrer-picker-none"
            className={T.pickerGhost}
          >
            I found My Silo myself
          </button>
        )}
      </div>

      {/* The list: silos this device has visited, newest first. */}
      {open && (
        <div className="mt-3 space-y-1.5" data-testid="referrer-picker-list">
          {options.length === 0 && (
            <div className={`text-xs ${T.muted}`}>No silos remembered on this device yet.</div>
          )}
          {options.map((o) => (
            <button
              key={o.slug}
              type="button"
              onClick={() => choose("pick", o.slug)}
              data-testid={`referrer-option-${o.slug}`}
              className={T.pickerOption}
            >
              <span className="min-w-0">
                <span className="block truncate font-medium">{o.name}</span>
          {o.creator && o.creator !== o.name ? <span className={`block truncate text-[10px] ${T.muted}`}>{o.creator}</span> : null}
              </span>
              {value?.slug === o.slug && <Check size={13} weight="bold" className="shrink-0" />}
            </button>
          ))}
          <p className={`text-[10px] ${T.muted} pt-1`}>
            Only silos you've opened on this device appear here. Don't see your friend? Visit their
            silo page once, then come back and sign up.
          </p>
        </div>
      )}
    </div>
  );
}
